import { useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Plane, RotateCcw } from "lucide-react";

const STEPS = [
  { key: "recipient", q: "Who are you gifting?", opts: ["For Her", "For Him", "For Myself", "A Friend"] },
  { key: "occasion", q: "What's the occasion?", opts: ["Everyday", "Date Night", "Wedding Guest", "Festive", "Party", "Birthday", "Anniversary", "Gifting"] },
  { key: "budget", q: "Your budget?", opts: ["Under ₹700", "₹700–₹1000", "₹1000–₹1500", "No limit"] },
  { key: "type", q: "Which piece?", opts: ["Rings", "Necklaces", "Earrings", "Bracelets", "Anklets", "Surprise Me"] },
];
const BUDGET = { "Under ₹700": [0, 700], "₹700–₹1000": [700, 1000], "₹1000–₹1500": [1000, 1500], "No limit": [0, 999999] };

export default function GiftFinder() {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const choose = async (key, val) => {
    const next = { ...ans, [key]: val };
    setAns(next);
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    setLoading(true);
    const params = {};
    if (next.occasion) params.occasion = next.occasion;
    if (next.type && next.type !== "Surprise Me") params.category = next.type;
    const [min, max] = BUDGET[next.budget] || [0, 999999];
    params.max_price = max; params.min_price = min;
    try {
      const { data } = await api.get("/products", { params });
      setResults(data);
    } catch { setResults([]); }
    setLoading(false);
  };

  const reset = () => { setStep(0); setAns({}); setResults(null); };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-20">
      <div className="border-b border-[#EAE2D3] bg-[#2C221E] py-14 text-center">
        <Plane size={22} className="mx-auto text-amber-400" />
        <h1 className="mt-3 font-serif text-4xl text-[#F4E7C5] sm:text-5xl">Gift Finder</h1>
        <p className="mt-2 text-sm text-stone-400">Let us curate the perfect PRIYARAA piece for your journey.</p>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        {results === null ? (
          <div data-testid="gift-finder-step" className="text-center">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-700">Step {step + 1} / {STEPS.length}</p>
            <h2 className="mt-3 font-serif text-3xl text-stone-900">{STEPS[step].q}</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {STEPS[step].opts.map((o) => (
                <button key={o} data-testid={`gift-opt-${o}`} onClick={() => choose(STEPS[step].key, o)}
                  className="rounded-full border border-[#E7DFD3] bg-white px-6 py-3 text-sm font-medium text-stone-700 transition-all hover:border-amber-500 hover:bg-amber-50">
                  {o}
                </button>
              ))}
            </div>
            {step > 0 && <button onClick={() => setStep(step - 1)} className="mt-8 text-sm text-stone-500 underline">Back</button>}
          </div>
        ) : (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-stone-900">Curated for you</h2>
              <button data-testid="gift-restart" onClick={reset} className="flex items-center gap-1.5 text-sm text-amber-700"><RotateCcw size={14} /> Start over</button>
            </div>
            {loading ? <p className="text-stone-500">Finding pieces…</p>
              : results.length === 0
                ? <p className="text-stone-500">No perfect match — <button onClick={reset} className="text-amber-700 underline">try again</button> with a wider budget.</p>
                : <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">{results.map((p) => <ProductCard key={p.id} product={p} />)}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
